import {
  faCheck,
  faEdit,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Button, Card, Col, Modal, Row, Table } from 'react-bootstrap';
import LoadingOverlay from '../../LoadingOverlay';
import AddAllStaffModal from '../../modals/AddAllStaffModal';
import UserPropertyGroupForm from '../UserPropertyGroupForm';

function Permissions(props) {
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserPropertyGroup, setSelectedUserPropertyGroup] = useState(
    null
  );

  /**
   * @param {int} userId
   * @param {string} userType
   * @returns {object|null}
   */
  const findUser = (userId, userType) => {
    if (userType === 'fruition') {
      return props.users.find((user) => user.id === userId);
    } else {
      return null;
    }
  };

  /**
   * Update the property group object by adding the supplied user property group
   * to the users array.
   *
   * @param {object} userPropertyGroup
   */
  const addUserPropertyGroup = (userPropertyGroup) => {
    const newPropertyGroup = { ...props.propertyGroup };
    newPropertyGroup.users.push(userPropertyGroup);
    props.update(newPropertyGroup);
  };

  /**
   * Update the property group object by adding the supplied user property groups
   * to the users array.
   *
   * @param {object[]} userPropertyGroup
   */
  const addUserPropertyGroups = (userPropertyGroups) => {
    const newPropertyGroup = { ...props.propertyGroup };
    newPropertyGroup.users.push(...userPropertyGroups);
    props.update(newPropertyGroup);
  };

  /**
   * Update the property group object by sending an API request, then if successful
   * finding and removing the supplied user property group from the users array.
   *
   * @param {object} userPropertyGroup
   */
  const removeUserPropertyGroup = (userPropertyGroup) => {
    const user = findUser(
      userPropertyGroup.user_id,
      userPropertyGroup.user_type
    );

    if (!user) {
      window.alert('Unknown user');
    } else if (
      window.confirm(`Are you sure you want to delete ${user.name}?`)
    ) {
      setLoadingPermissions(true);
      fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/user-property-group/${userPropertyGroup.id}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + props.bearerToken,
            SeasonalDatabase: props.selectedDatabase
          }
        }
      )
        .then((res) => {
          if (res.ok) {
            const newPropertyGroup = { ...props.propertyGroup };
            newPropertyGroup.users.splice(
              newPropertyGroup.users.findIndex(
                (u) => u.id === userPropertyGroup.id
              ),
              1
            );
            props.update(newPropertyGroup);
          } else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          setLoadingPermissions(false);
        });
    }
  };

  /**
   * Update the property group object by finding and updating the supplied user
   * property group in the users array.
   *
   * @param {object} userPropertyGroup
   */
  const updateUserPropertyGroup = (userPropertyGroup) => {
    const newPropertyGroup = { ...props.propertyGroup };
    newPropertyGroup.users[
      newPropertyGroup.users.findIndex((u) => u.id === userPropertyGroup.id)
    ] = userPropertyGroup;
    props.update(newPropertyGroup);
  };

  const handleSubmitUserPermissions = (values, actions) => {
    fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/user-property-group${
        selectedUserPropertyGroup ? `/${selectedUserPropertyGroup.id}` : ''
      }?with[]=propertyRole`,
      {
        method: selectedUserPropertyGroup ? 'PUT' : 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          SeasonalDatabase: props.selectedDatabase,
          Authorization: `Bearer ${props.bearerToken}`
        },
        body: JSON.stringify({
          property_group_id: props.propertyGroup.id,
          user_type: 'fruition',
          user_id: values.user.value,
          can_edit: !!values.can_edit,
          property_role_id: values.propertyRole.value
        })
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          if (selectedUserPropertyGroup) {
            updateUserPropertyGroup(data);
          } else {
            addUserPropertyGroup(data);
          }

          setShowAddUserModal(false);
          setSelectedUserPropertyGroup(null);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  const handleAddAllStaffConfirmation = () => {
    return fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/user-property-group/batch`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          SeasonalDatabase: props.selectedDatabase,
          Authorization: `Bearer ${props.bearerToken}`
        },
        body: JSON.stringify({
          property_group_id: props.propertyGroup.id
        })
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          addUserPropertyGroups(data);
          setSelectedUserPropertyGroup(null);
        }
      });
  };

  return (
    <Card>
      <Card.Header
        style={{ height: '63px' }}
        className='d-flex align-items-center'
      >
        Permissions
      </Card.Header>
      <Card.Body>
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Can Edit Group?</th>
              <th>Assigned Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.propertyGroup.users.map((userPropertyGroup) => {
              const user = findUser(
                userPropertyGroup.user_id,
                userPropertyGroup.user_type
              );

              if (user) {
                return (
                  <tr key={userPropertyGroup.id}>
                    <td>{user.name}</td>
                    <td>
                      <FontAwesomeIcon
                        className={
                          userPropertyGroup.can_edit
                            ? 'text-success'
                            : 'text-danger'
                        }
                        icon={userPropertyGroup.can_edit ? faCheck : faTimes}
                      />
                    </td>
                    <td>{userPropertyGroup.property_role.name}</td>
                    <td>
                      <Button
                        variant='link'
                        className='text-danger'
                        onClick={() => {
                          removeUserPropertyGroup(userPropertyGroup);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                      <Button
                        variant='link'
                        className='text-primary'
                        onClick={() => {
                          setSelectedUserPropertyGroup(userPropertyGroup);
                          setShowAddUserModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </td>
                  </tr>
                );
              }
            })}
          </tbody>
        </Table>
        <Row>
          <Col md={5}>
            <Button
              variant='outline-success'
              onClick={() => {
                setShowAddUserModal(true);
              }}
            >
              Add User
            </Button>
          </Col>
          <Col></Col>
          {/** Add all staff modal */}
          <Col md={5}>
            <AddAllStaffModal
              handleAddAllStaffConfirmation={handleAddAllStaffConfirmation}
            />
          </Col>
        </Row>
        {/** Add user modal */}
        <Modal
          backdrop='static'
          show={showAddUserModal}
          onHide={() => {
            setShowAddUserModal(false);
            setSelectedUserPropertyGroup(null);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedUserPropertyGroup ? 'Edit ' : 'Add '}User
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              initialValues={{
                can_edit: selectedUserPropertyGroup
                  ? selectedUserPropertyGroup.can_edit
                  : false,

                // react-select objects
                user: selectedUserPropertyGroup
                  ? {
                      value: selectedUserPropertyGroup.user_id,
                      label: props.users.find(
                        (user) => user.id === selectedUserPropertyGroup.user_id
                      ).name
                    }
                  : null,
                propertyRole: selectedUserPropertyGroup
                  ? {
                      value: selectedUserPropertyGroup.property_role.id,
                      label: selectedUserPropertyGroup.property_role.name
                    }
                  : null,

                // react-select list data
                users: selectedUserPropertyGroup ? null : props.users,
                propertyRoles: props.propertyRoles
              }}
              validate={(values) => {
                const errors = {};

                if (!values.user) errors.user = 'This is a required field';
                if (!values.propertyRole)
                  errors.propertyRole = 'This is a required field';

                return errors;
              }}
              onSubmit={handleSubmitUserPermissions}
            >
              {(props) => (
                <Fragment>
                  <UserPropertyGroupForm {...props} />
                  <Button
                    variant='primary'
                    type='button'
                    onClick={props.handleSubmit}
                  >
                    Submit
                  </Button>
                  {props.isSubmitting ? <LoadingOverlay /> : null}
                </Fragment>
              )}
            </Formik>
          </Modal.Body>
        </Modal>
        {loadingPermissions ? <LoadingOverlay /> : null}
      </Card.Body>
    </Card>
  );
}

Permissions.propTypes = {
  // Authentication
  bearerToken: PropTypes.string.isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  // databases: PropTypes.array.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Property Group
  propertyGroup: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,

  // Misc
  users: PropTypes.array.isRequired,
  propertyRoles: PropTypes.array.isRequired
};

export default Permissions;
